import { useEffect, useState } from 'react'
import { SectionTitle } from '@/components/custom/deal-review/panel-primitives'
import {
  AlertTriangle,
  CloudOff,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Send,
  X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  isInProgress,
  isRescreening,
} from '@/features/deal-review/deal-lead-views'
import { LeadProcessingProgress } from '@/components/custom/deal-review/lead-processing-progress'
import { ReanalysisActions } from '@/components/custom/deal-review/deal-lead-reanalysis-actions'
import { AddNamePopover } from '@/components/custom/deal-review/deal-lead-naming'
import {
  CriteriaTable,
  OpenQuestions,
} from '@/components/custom/deal-review/criteria-table'
import { openQuestionsTitle } from '@/features/deal-review/criterion-evidence'
import { ExtractedFields } from '@/components/custom/deal-review/extracted-fields'
import {
  dealCloudEntryUrl,
  reportSendError,
  reportSendOutcome,
  sendIsReady,
  sendNote,
  sendFailureMessage,
  sendPartialResultMessage,
  sentDealCloudEntryId,
} from '@/features/deal-review/deal-lead-views'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import {
  displayLabelFor,
  displayNameFor,
  TITLE_CLASS,
  processingStatusFor,
  syncStatusFor,
} from '@/features/deal-review/deal-lead-views'
import { evidenceAbsence } from '@/features/deal-review/deal-lead-fields'
import {
  useDealLead,
  useDealLeadCounts,
  useIsRescreening,
  usePrefetchAdjacentLeads,
  useSendToDealCloud,
} from '@/hooks/use-deal-leads'
import { DealLeadDecisionForm } from '@/components/custom/deal-review/deal-lead-decision-form'
import { STATUS_TONE_CLASSES } from '@/features/deal-review/review-tones'
import { formatReceivedAt } from '@/features/deal-review/deal-lead-fields'
import type { DealLeadDetail } from '@/types/api'
import { SourceEmailTab } from '@/components/custom/deal-review/source-email-tab'
import { SourceFilesSection } from '@/components/custom/deal-review/source-files-section'
import { ActivitySection } from '@/components/custom/deal-review/activity-section'

function SendToDealCloudAction({
  lead,
  onSent,
}: {
  lead: DealLeadDetail
  onSent?: (detail: DealLeadDetail) => void
}) {
  const counts = useDealLeadCounts()
  const send = useSendToDealCloud(lead.id)
  const writeEnabled = counts.data?.dealCloudWriteEnabled

  if (sentDealCloudEntryId(lead)) return null

  const ready = sendIsReady(lead, writeEnabled)
  const blocked = !ready || send.isPending
  const explanation =
    lead.capabilities.sendBlockedReason ??
    sendNote(lead) ??
    'Creates or updates the Deal in DealCloud.'

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            size="sm"
            // aria-disabled, not disabled: a disabled button can't open the tooltip explaining why.
            aria-disabled={blocked}
            className="h-7.5 shrink-0 gap-1.5 rounded-full px-2.75 text-xs aria-disabled:border aria-disabled:border-primary/15 aria-disabled:bg-transparent aria-disabled:text-primary/45 aria-disabled:shadow-none [&_svg]:size-3"
            onClick={() => {
              if (blocked) return
              send.mutate(
                { version: lead.version },
                {
                  onSuccess: (detail) => {
                    reportSendOutcome(detail)
                    onSent?.(detail)
                  },
                  onError: reportSendError,
                }
              )
            }}
          >
            <Send aria-hidden="true" />
            {send.isPending ? 'Sending…' : 'Send to DealCloud'}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{explanation}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function SendNote({ lead }: { lead: DealLeadDetail }) {
  const sentEntryId = sentDealCloudEntryId(lead)
  if (!sentEntryId) return null

  return (
    <Typography
      variant="body-sm"
      className="mt-2 leading-[1.4] text-primary/70"
    >
      Sent to DealCloud as {sentEntryId}
    </Typography>
  )
}

// Height pinned: the button's line-height resolves to the inherited 1.5 whatever is set.
const TAB_TRIGGER_CLASSES =
  'h-9.25 rounded-none border-0 border-b-2 px-2.5 py-0 text-sm text-muted-foreground data-[state=active]:border-secondary data-[state=active]:font-bold data-[state=active]:text-primary data-[state=active]:shadow-none'

function DealCloudBadge({ lead }: { lead: DealLeadDetail }) {
  const sync = syncStatusFor(lead)
  if (!sync) return null

  if (lead.dealCloudSyncState === 'NOT_SENT') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            tabIndex={0}
            aria-label={sync.label}
            className="inline-flex size-5 items-center justify-center rounded-sm text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CloudOff aria-hidden="true" className="size-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{sync.label}</TooltipContent>
      </Tooltip>
    )
  }

  if (sync.tone === 'negative') {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label={`${sync.label}. ${sendFailureMessage(lead)}`}
            className="inline-flex size-5 items-center justify-center text-destructive"
          >
            <AlertTriangle aria-hidden="true" className="size-3.5" />
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-64">
          {sync.label}: {sendFailureMessage(lead)}
        </TooltipContent>
      </Tooltip>
    )
  }

  const sentEntryId = sentDealCloudEntryId(lead)
  if (!sentEntryId) {
    return (
      <Badge variant="outline" className={STATUS_TONE_CLASSES[sync.tone]}>
        {sync.label}
      </Badge>
    )
  }

  const label = `Sent to DealCloud as ${sentEntryId}`
  const url = dealCloudEntryUrl(sentEntryId)

  if (!url) {
    return (
      <Badge variant="outline" className={STATUS_TONE_CLASSES[sync.tone]}>
        {label}
      </Badge>
    )
  }

  return (
    <Button
      asChild
      size="sm"
      variant="outline"
      className="h-5 gap-1 rounded-full px-2 py-0 text-xs font-normal leading-5 [&_svg]:size-2.5"
    >
      <a href={url} target="_blank" rel="noreferrer" aria-label={label}>
        <ExternalLink aria-hidden="true" />
        View in DealCloud
      </a>
    </Button>
  )
}

function PartialSendNote({ lead }: { lead: DealLeadDetail }) {
  const message = sendPartialResultMessage(lead)
  if (!message) return null

  return (
    <Typography
      variant="body-sm"
      className="mt-2 max-w-[62ch] leading-[1.4] text-primary/70"
    >
      {message}
    </Typography>
  )
}

function StepToLead({
  leadId,
  onNavigate,
  label,
  shortcut,
  icon: Icon,
}: {
  leadId: string | null | undefined
  onNavigate: (leadId: string) => void
  label: string
  shortcut: string
  icon: LucideIcon
}) {
  const blocked = !leadId

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={label}
            aria-disabled={blocked}
            className="size-7 rounded-md text-primary/70 hover:text-primary aria-disabled:text-primary/30 aria-disabled:hover:bg-transparent [&_svg]:size-4"
            onClick={() => leadId && onNavigate(leadId)}
          >
            <Icon />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {blocked ? `No ${label.toLowerCase()}` : `${label} (${shortcut})`}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function DetailHeader({
  lead,
  reworking,
  screening,
  onClose,
  onSent,
  previousLeadId,
  nextLeadId,
  onNavigate,
}: {
  lead: DealLeadDetail | undefined
  reworking: boolean
  screening: boolean
  onClose: () => void
  onSent?: (detail: DealLeadDetail) => void
  previousLeadId?: string | null
  nextLeadId?: string | null
  onNavigate?: (leadId: string) => void
}) {
  const status = screening
    ? { label: 'Screening', tone: 'neutral' as const }
    : lead
      ? processingStatusFor(lead)
      : null
  const sender = lead?.message.senderName ?? lead?.message.senderEmail ?? null
  const title = lead ? displayLabelFor(lead) : 'Deal lead'
  const needsName =
    lead !== undefined &&
    !reworking &&
    displayNameFor(lead) === null &&
    lead.capabilities.canCorrect

  return (
    <div className="bg-white px-5.5 pb-3.5 pt-4.5">
      <div className="flex items-start justify-between gap-4">
        {/* Leading rides with the size: a separate leading-* class loses to the variant's own. */}
        {lead ? (
          <Typography
            variant="body-sm"
            className="min-w-0 truncate pt-1 text-xs/[1.2] text-primary/70"
          >
            {[sender, `Received ${formatReceivedAt(lead.message.receivedAt)}`]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        ) : (
          <Skeleton className="mt-1 h-[14.4px] w-44" />
        )}

        <div className="flex shrink-0 items-center gap-3.5">
          {onNavigate && (
            <div className="flex items-center">
              <StepToLead
                leadId={previousLeadId}
                onNavigate={onNavigate}
                label="Previous lead"
                shortcut="↑"
                icon={ChevronUp}
              />
              <StepToLead
                leadId={nextLeadId}
                onNavigate={onNavigate}
                label="Next lead"
                shortcut="↓"
                icon={ChevronDown}
              />
            </div>
          )}
          {lead && !reworking && <DealCloudBadge lead={lead} />}
          {lead && !reworking && (
            <SendToDealCloudAction lead={lead} onSent={onSent} />
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Close deal details"
            className="-m-1.75 size-auto rounded-lg p-1.75 text-primary/75 [&_svg]:size-4.5"
            onClick={onClose}
          >
            <X />
          </Button>
        </div>
      </div>

      {lead ? (
        <Typography
          variant="h2"
          className={cn(
            'mt-1.25 text-balance',
            TITLE_CLASS,
            needsName && 'text-primary/60'
          )}
        >
          {title}
        </Typography>
      ) : (
        <Skeleton className="mt-1.25 h-[28.75px] w-60" />
      )}

      {!lead && <Skeleton className="mt-2.25 h-6 w-28 rounded-full" />}
      {lead && (
        <div className="mt-2.25 flex flex-wrap items-center gap-1.75">
          {status && (
            <Badge
              variant="outline"
              className={cn('gap-1.5', STATUS_TONE_CLASSES[status.tone])}
            >
              {reworking && (
                <span className="relative flex size-1.5">
                  <span className="absolute inline-flex size-full animate-ping rounded-full bg-sky-500 opacity-60" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-sky-600" />
                </span>
              )}
              {status.label}
            </Badge>
          )}
          {needsName && <AddNamePopover lead={lead} />}
          {!reworking && <ReanalysisActions lead={lead} />}
        </div>
      )}
      {lead && !reworking && <PartialSendNote lead={lead} />}
      {lead && !reworking && <SendNote lead={lead} />}
    </div>
  )
}

function DetailSheetSkeleton() {
  const tabWidths = [58, 46, 44, 74, 50]

  return (
    <>
      <div className="shrink-0">
        <div className="mx-5.5 rounded-[12px] bg-sidebar p-4">
          <Skeleton className="h-3.25 w-4/5 bg-primary/10" />
          <Skeleton className="mt-1.5 h-3.25 w-2/5 bg-primary/10" />
          <div className="mt-3 grid grid-cols-2 gap-1.75">
            <Skeleton className="h-11 rounded-[9px] bg-primary/10" />
            <Skeleton className="h-11 rounded-[9px] bg-primary/10" />
          </div>
          <div className="mt-3 flex items-center justify-between">
            <Skeleton className="h-3 w-28 bg-primary/10" />
            <Skeleton className="h-3 w-20 bg-primary/10" />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex h-9.25 items-center gap-0.5 border-b border-primary/10 bg-white px-5.5 pt-3.5">
          {tabWidths.map((width, index) => (
            <div key={index} className="px-2.5">
              <Skeleton className="h-3" style={{ width }} />
            </div>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-hidden p-5.5">
          <Skeleton className="h-3.25 w-24" />
          <div className="mt-2 rounded-lg border border-primary/10 px-3.5 py-3">
            <Skeleton className="h-3.25 w-2/5" />
            <Skeleton className="mt-2 h-3 w-full" />
            <Skeleton className="mt-1.5 h-3 w-3/4" />
          </div>
          <div className="mt-6.5 flex items-center justify-between border-y border-primary/10 py-3.5">
            <Skeleton className="h-3.25 w-36" />
            <Skeleton className="h-3 w-6" />
          </div>
        </div>
      </div>
    </>
  )
}

function DetailTabs({ lead }: { lead: DealLeadDetail }) {
  const absence = evidenceAbsence(lead)
  const run = lead.latestScreeningRun
  const openTitle = run ? openQuestionsTitle(run) : null

  return (
    <Tabs defaultValue="overview" className="flex min-h-0 flex-1 flex-col">
      <div className="overflow-x-auto border-b border-primary/10 bg-white px-5.5 pt-3.5">
        <TabsList className="h-auto min-w-max gap-0.5 rounded-none bg-transparent p-0">
          <TabsTrigger value="overview" className={TAB_TRIGGER_CLASSES}>
            Overview
          </TabsTrigger>
          <TabsTrigger value="criteria" className={TAB_TRIGGER_CLASSES}>
            Criteria
          </TabsTrigger>
          <TabsTrigger value="source" className={TAB_TRIGGER_CLASSES}>
            Source
          </TabsTrigger>
          <TabsTrigger value="attachments" className={TAB_TRIGGER_CLASSES}>
            Attachments
          </TabsTrigger>
          <TabsTrigger value="activity" className={TAB_TRIGGER_CLASSES}>
            Activity
          </TabsTrigger>
        </TabsList>
      </div>

      <div className="scrollbar-visible min-h-0 flex-1 overflow-y-auto">
        <TabsContent value="overview" className="m-0 space-y-6.5 p-5.5">
          {absence !== null && (
            <Typography variant="body-sm" className="text-primary/70 italic">
              {absence === 'NOT_EXTRACTED'
                ? 'This lead has not been extracted yet, so there are no values or quotes to show.'
                : 'This extraction recorded no source quotes, so the values below cannot be traced back to the email.'}
            </Typography>
          )}
          {run && openTitle && (
            <section>
              <SectionTitle>{openTitle}</SectionTitle>
              <OpenQuestions run={run} lead={lead} withEvidence />
            </section>
          )}
          <ExtractedFields lead={lead} />
        </TabsContent>

        <TabsContent value="criteria" className="m-0 p-5.5">
          {run ? (
            <>
              <CriteriaTable run={run} lead={lead} />
              {openTitle && (
                <section className="mt-6">
                  <SectionTitle>In detail</SectionTitle>
                  <OpenQuestions run={run} lead={lead} withEvidence={false} />
                </section>
              )}
            </>
          ) : (
            <Typography variant="body-sm" className="italic text-primary/70">
              No criteria ran on this lead — it has not been screened yet.
            </Typography>
          )}
        </TabsContent>

        <TabsContent value="source" className="m-0 p-5.5">
          <SourceEmailTab lead={lead} />
        </TabsContent>

        <TabsContent value="attachments" className="m-0 space-y-3 p-5.5">
          <SourceFilesSection lead={lead} />
        </TabsContent>

        <TabsContent value="activity" className="m-0 p-5.5">
          <ActivitySection lead={lead} />
        </TabsContent>
      </div>
    </Tabs>
  )
}

export function DealLeadDetailSheet({
  selectedLeadId,
  onClose,
  onDecided,
  onSent,
  previousLeadId = null,
  nextLeadId = null,
  onNavigate,
}: {
  selectedLeadId: string | null
  onClose: () => void
  onDecided?: (detail: DealLeadDetail) => void
  onSent?: (detail: DealLeadDetail) => void
  previousLeadId?: string | null
  nextLeadId?: string | null
  onNavigate?: (leadId: string) => void
}) {
  const detail = useDealLead(selectedLeadId ?? undefined)
  const lead = detail.data

  usePrefetchAdjacentLeads([previousLeadId, nextLeadId])

  // Held here, not in the card: the card remounts per lead and the fold should persist.
  const [decisionCollapsed, setDecisionCollapsed] = useState(false)

  // Skip typed fields, open Radix poppers (portalled, so check document) and modified keys.
  useEffect(() => {
    if (!selectedLeadId || !onNavigate) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
      if (event.defaultPrevented) return
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return
      }

      const target = event.target
      if (target instanceof HTMLElement) {
        if (target.isContentEditable) return
        if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      }
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return

      const to = event.key === 'ArrowUp' ? previousLeadId : nextLeadId
      if (!to) return

      event.preventDefault()
      onNavigate(to)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [selectedLeadId, previousLeadId, nextLeadId, onNavigate])

  const asking = useIsRescreening(selectedLeadId ?? undefined)
  const reworking = lead !== undefined && (isInProgress(lead) || asking)
  const screening = reworking && lead !== undefined && isRescreening(lead)

  return (
    <Sheet
      open={Boolean(selectedLeadId)}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        side="right"
        hideClose
        // Radix would autofocus Previous and pop its tooltip on open; focus the panel instead.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.focus()
          }
        }}
        className="flex w-full flex-col gap-0 border-primary/15 bg-background p-0 shadow-2xl sm:max-w-140"
      >
        <SheetTitle className="sr-only">
          {lead ? (displayNameFor(lead) ?? 'Deal lead') : 'Deal lead'}
        </SheetTitle>
        <SheetDescription className="sr-only">
          Review the extracted overview, source email, attachments, activity and
          screening criteria for this lead.
        </SheetDescription>

        <DetailHeader
          lead={lead}
          reworking={reworking}
          screening={screening}
          onClose={onClose}
          onSent={onSent}
          previousLeadId={previousLeadId}
          nextLeadId={nextLeadId}
          onNavigate={onNavigate}
        />

        {detail.isLoading && <DetailSheetSkeleton />}

        {detail.isError && (
          <div className="p-5">
            <Typography variant="body-sm" className="text-destructive">
              This lead could not be loaded.
            </Typography>
          </div>
        )}

        {lead && reworking && (
          <div className="min-h-0 flex-1 overflow-y-auto p-5">
            <LeadProcessingProgress lead={lead} />
          </div>
        )}

        {lead && !reworking && (
          <>
            <div className="max-h-[50vh] shrink-0 overflow-y-auto">
              <DealLeadDecisionForm
                key={lead.id}
                lead={lead}
                onDecided={onDecided}
                collapsed={decisionCollapsed}
                onCollapsedChange={setDecisionCollapsed}
              />
            </div>
            <DetailTabs lead={lead} />
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
